import { register } from '../apiRegistry';
import apiService from '../apiService';

const getStudentId = () => localStorage.getItem('studentId');

register({
  'user/caseCrumbs': async () => {
    return await apiService.get('casecrumbs');
  },

  'user/caseCrumbs-artifact': async params => {
    return await apiService.get(`casecrumbs/${params?.caseId}/artifact/content`);
  },

  'user/profile': async () => {
    return await apiService.get(`/users/${getStudentId()}`);
  },

  'user/overallProgress': async () => {
    return await apiService.get(`/activities/summary`);
  },

  'user/performance-report': async params => {
    return await apiService.get(`activities/${params?.activityId}`);
  },

  'user/contact-form': async payload => {
    return await apiService.post('email/send-contact-form', payload);
  },
  'user/feedback': async payload => {
    return await apiService.post('activities', payload);
  },

  'user/activities-create': async payload => {
    return await apiService.post('activities', payload);
  },
  'user/activities-list': async params => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.reference_id) queryParams.append('reference_id', params.reference_id);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    return await apiService.get(`activities?${queryParams.toString()}`);
  },

  'admin/reporting': async () => {
    return await apiService.get(`activities/reporting?page=1&limit=10000&include_activities=false`);
  },
  'admin/task-assessment-scores': async () => {
    return await apiService.get(`reports/activities?activity_type=sustainskills&limit=1000&format=json`);
  },
  'admin/programs': async () => {
    return await apiService.get(`program?status=active&limit=10000`);
  },
  'admin/programs-list': async (params) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.code) queryParams.append('code', params.code);
    queryParams.append('limit', params?.limit?.toString() || '10');
    queryParams.append('offset', params?.offset?.toString() || '0');
    
    return await apiService.get(`program?${queryParams.toString()}`);
  },

  'admin/program-detail': async (params) => {
    return await apiService.get(`program/${params?.programId}`);
  },

  'admin/program-create': async (payload) => {
    return await apiService.post('program', payload);
  },

  'admin/program-update': async (params) => {
    return await apiService.put(`program/${params?.programId}`, params?.data);
  },

  'admin/program-delete': async (params) => {
    return await apiService.delete(`program/${params?.programId}`);
  },

  'admin/program-activate': async (params) => {
    return await apiService.post(`program/${params?.programId}/activate`);
  },

  'admin/program-deactivate': async (params) => {
    return await apiService.post(`program/${params?.programId}/deactivate`);
  },

  'admin/program-statistics': async () => {
  return await apiService.get('program/stats/summary');
  },

  'admin/program-bulk-import-template': async () => {
    return await apiService.get('program/bulk-import/template');
  },

  'admin/program-bulk-import': async (payload) => {
    return await apiService.post('program/bulk-import', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  'admin/users-list': async (params) => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.includeDeleted) queryParams.append('include_deleted', 'true');
    queryParams.append('limit', params?.limit?.toString() || '10');
    queryParams.append('offset', params?.offset?.toString() || '0');
    
    const response = await apiService.get(`users?${queryParams.toString()}`);
    
    // Transform response to match expected structure
    return {
      data: response.users || [],
      total: response.pagination.total || 0,
      limit: params?.limit || 10,
      offset: params?.offset || 0
    };
  },

  'admin/user-detail': async (params) => {
    return await apiService.get(`users/${params?.userId}`);
  },

  'admin/user-create': async (payload) => {
    return await apiService.post('users', payload);
  },

  'admin/user-update': async (params) => {
    return await apiService.put(`users/${params?.userId}`, params?.data);
  },

  'admin/user-soft-delete': async (params) => {
    return await apiService.delete(`users/${params?.userId}`);
  },

  'admin/user-hard-delete': async (params) => {
    return await apiService.delete(`users/${params?.userId}?hard=true`);
  },

  'admin/user-statistics': async () => {
    return await apiService.get('users/stats/summary');
  },

  'admin/reset-password': async (payload) => {
    return await apiService.post('auth/reset-password', payload);
  },

  'admin/courses-list': async (params) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.code) queryParams.append('code', params.code);
    queryParams.append('limit', params?.limit?.toString() || '5');
    queryParams.append('offset', params?.offset?.toString() || '0');
    
    const response = await apiService.get(`course?${queryParams.toString()}`);
    
    return {
      data: response.courses || [],
      total: response.total || 0,
      limit: params?.limit || 10,
      offset: params?.offset || 0
    };
  },

  'admin/course-detail': async (params) => {
    return await apiService.get(`course/${params?.courseId}`);
  },

  'admin/course-create': async (payload) => {
    return await apiService.post('course', payload);
  },

  'admin/course-update': async (params) => {
    return await apiService.put(`course/${params?.courseId}`, params?.data);
  },

  'admin/course-delete': async (params) => {
    return await apiService.delete(`course/${params?.courseId}`);
  },

  'admin/course-activate': async (params) => {
    return await apiService.post(`course/${params?.courseId}/activate`);
  },

  'admin/course-deactivate': async (params) => {
    return await apiService.post(`course/${params?.courseId}/deactivate`);
  },

  // ProgramCourse Mapping Operations
  'admin/program-course-create': async (payload) => {
    return await apiService.post('program_course', payload);
  },

  'admin/program-courses': async (params) => {
    return await apiService.get(`program_course/program/${params?.programId}/courses`);
  },

  'admin/course-programs': async (params) => {
    return await apiService.get(`program_course/course/${params?.courseId}/programs`);
  },

  'admin/program-course-update': async (params) => {
    return await apiService.put(`program_course/program/${params?.programId}/course/${params?.courseId}`, params?.data);
  },

  'admin/program-course-delete': async (params) => {
    return await apiService.delete(`program_course/program/${params?.programId}/course/${params?.courseId}`);
  },

  'admin/course-statistics': async () => {
  return await apiService.get('course/stats/summary');
  },

  'admin/user-restore': async (params) => {
    return await apiService.post(`users/${params?.userId}/restore`);
  },
  
  'admin/bulk-import-template': async () => {
    return await apiService.get('users/bulk-import/template');
  },

  'admin/bulk-import-users': async (payload) => {
    return await apiService.post('users/bulk-import', payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  'user/sustainSkills': async () => {
    const result: any = await apiService.get('sustainskills?limit=1000');
    const userRole = JSON.parse(localStorage.getItem('user') || '{}')?.role;
    if (userRole === 'admin') return result;
    const studentId = localStorage.getItem('studentId');
    if (!studentId) return result;
    try {
      const userData: any = await apiService.get(`users/${studentId}`);
      const enrolledCourses: { course_id: string }[] = userData?.metadata?.enrolled_courses || [];
      if (enrolledCourses.length === 0) return result;
      const enrolledIds = new Set(enrolledCourses.map((c: { course_id: string }) => c.course_id));
      return { ...result, sustainskills: (result.sustainskills || []).filter((ss: any) => enrolledIds.has(ss.course_id)) };
    } catch {
      return result;
    }
  },
   
  'user/sustainSkills-artifact': async params => {
    return await apiService.get(`sustainskills/${params?.id}/artifact/content`);
  },
  
  'admin/enrollment-list': async params => {
    const queryParams = new URLSearchParams();

    // Optional filters
    if (params?.type) queryParams.append('type', params.type);                // program | course
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);          // ENROLLED | VERIFIED | FAILED | UNENROLLED
    if (params?.user_id) queryParams.append('user_id', params.user_id);
    if (params?.program_id) queryParams.append('program_id', params.program_id);
    if (params?.course_id) queryParams.append('course_id', params.course_id);

    // Pagination
    queryParams.append('page', params?.page?.toString() || '1');
    queryParams.append('limit', params?.limit?.toString() || '10');

    // API call
    const response = await apiService.get(`/enrollment/query?${queryParams.toString()}`);
    // console.log("response: ", response);

    return {
      data: response.enrollments || [],
      total: response?.total || 0,
      page: params?.page || 1,
      limit: params?.limit || 10
    };
  },

  'admin/enrollment-statistics': async () => {
    return await apiService.get('/enrollment/stats/summary');
  },

  'admin/enrollment-enroll-program': async (payload) => {
    return await apiService.post('/enrollment/program', payload);
  },

  'admin/enrollment-enroll-course': async (payload) => {
    return await apiService.post('/enrollment/course', payload);
  },

  'admin/enrollment-unenroll-program': async (payload) => {
    return await apiService.post('/enrollment/program/unenroll', payload);
  },

  'admin/enrollment-unenroll-course': async (payload) => {
    return await apiService.post('/enrollment/course/unenroll', payload);
  },

  'admin/enrollment-unenroll-by-id': async (params) => {
    return await apiService.post(`/enrollment/unenroll/${params?.enrollmentId}`);
  },

  'admin/enrollment-user-programs': async (params) => {
    return await apiService.get(`/enrollment/program/${params?.userId}`);
  },

  'admin/enrollment-user-courses': async (params) => {
    return await apiService.get(`/enrollment/course/${params?.userId}`);
  },

  'admin/email-templates-list': async params => {
    const queryParams = new URLSearchParams();

    // Optional filters
    if (params?.name) queryParams.append('name', params.name);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active);
    if (params?.search) queryParams.append('search', params.search);

    queryParams.append('limit', params?.limit?.toString() || '50');
    queryParams.append('offset', params?.offset?.toString() || '0');

    const response = await apiService.get(`/email/templates?${queryParams.toString()}`);
    // console.log(response.data);

    return {
      data: response?.templates || [],
      total: response?.templates?.length || 0,   // API does not provide total count explicitly
      limit: params?.limit || 50,
      offset: params?.offset || 0
    };
  },

  'admin/email-logs-list': async params => {
    const queryParams = new URLSearchParams();

    // Optional filters
    if (params?.to_email) queryParams.append('to_email', params.to_email);
    if (params?.from_email) queryParams.append('from_email', params.from_email);
    if (params?.status) queryParams.append('status', params.status);                // sent | delivered | bounced | complained | failed
    if (params?.user_id) queryParams.append('user_id', params.user_id);             // admin only
    if (params?.template_name) queryParams.append('template_name', params.template_name);
    if (params?.from_date) queryParams.append('from_date', params.from_date);       // ISO string
    if (params?.to_date) queryParams.append('to_date', params.to_date);             // ISO string
    if (params?.search) queryParams.append('search', params.search);                // subject/body search

    queryParams.append('limit', params?.limit?.toString() || '20');
    queryParams.append('offset', params?.offset?.toString() || '0');

    const response = await apiService.get(`/email/logs?${queryParams.toString()}`);

    return {
      data: response?.logs || [],
      total: response?.total || 0,
      limit: params?.limit || 20,
      offset: params?.offset || 0
    };
  },

  // Email Template Operations
  'admin/email-template-statistics': async () => {
    return await apiService.get('/email/templates/stats/summary');
  },

  'admin/email-template-detail': async (params) => {
    return await apiService.get(`/email/templates/${params?.templateId}`);
  },

  'admin/email-template-create': async (payload) => {
    return await apiService.post('/email/templates', payload);
  },

  'admin/email-template-update': async (params) => {
    return await apiService.put(`/email/templates/${params?.templateId}`, params?.data);
  },

  'admin/email-template-delete': async (params) => {
    return await apiService.delete(`/email/templates/${params?.templateId}`);
  },

  // Email Log Operations
  'admin/email-log-statistics': async () => {
    return await apiService.get('/email/stats');
  },

  'admin/email-log-detail': async (params) => {
    return await apiService.get(`/email/logs/${params?.logId}`);
  },

  'admin/email-resend': async (params) => {
    return await apiService.post(`/email/resend/${params?.logId}`);
  },


});